// Manager Approval Queue Page (VAC-8)
// Displays pending vacation requests for manager approval

import React, { useState, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { RefreshCw, Calendar as CalendarIcon } from 'lucide-react'
import ProtectedRoute from '@/components/ProtectedRoute'
import { ApprovalRequestCard, ApprovalRequestCardSkeleton } from '@/components/vacation/ApprovalRequestCard'
import { ApprovalConfirmationModal } from '@/components/vacation/ApprovalConfirmationModal'
import { RequestContextPanel } from '@/components/vacation/RequestContextPanel'
import { useApprovalQueue, useApproveRequest, useRejectRequest } from '@/hooks/useApprovalQueue'
import type { ApprovalQueueItem } from '@/types/vacation'

const ApprovalQueuePage: React.FC = () => {
  const navigate = useNavigate()
  const [selectedRequest, setSelectedRequest] = useState<ApprovalQueueItem | null>(null)
  const [modalMode, setModalMode] = useState<'approve' | 'reject' | null>(null)
  const [showContextPanel, setShowContextPanel] = useState(false)
  const [showToast, setShowToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  // Hooks
  const { data: queueData, isLoading, error, refetch } = useApprovalQueue(1, 20)
  const approveRequest = useApproveRequest()
  const rejectRequest = useRejectRequest()

  // Handlers
  const handleApprove = useCallback((requestId: string) => {
    const request = queueData?.items.find(r => r.requestId === requestId)
    if (request) {
      setSelectedRequest(request)
      setModalMode('approve')
    }
  }, [queueData])

  const handleReject = useCallback((requestId: string) => {
    const request = queueData?.items.find(r => r.requestId === requestId)
    if (request) {
      setSelectedRequest(request)
      setModalMode('reject')
    }
  }, [queueData])

  const handleViewDetails = useCallback((requestId: string) => {
    const request = queueData?.items.find(r => r.requestId === requestId)
    if (request) {
      setSelectedRequest(request)
      setShowContextPanel(true)
    }
  }, [queueData])

  const handleCloseContextPanel = useCallback(() => {
    setShowContextPanel(false)
    setSelectedRequest(null)
  }, [])

  const handleConfirmAction = async (notes?: string, reason?: string) => {
    if (!selectedRequest) return

    try {
      if (modalMode === 'approve') {
        await approveRequest.mutate(selectedRequest.requestId, { approvalNotes: notes })
        setShowToast({
          message: `Férias aprovadas para ${selectedRequest.employeeName}`,
          type: 'success'
        })
      } else if (modalMode === 'reject') {
        await rejectRequest.mutate(selectedRequest.requestId, { rejectionReason: reason || '' })
        setShowToast({
          message: `Férias rejeitadas para ${selectedRequest.employeeName}`,
          type: 'success'
        })
      }

      // Close modal and refresh queue
      setModalMode(null)
      setSelectedRequest(null)
      await refetch()

      // Auto-dismiss toast after 5 seconds
      setTimeout(() => setShowToast(null), 5000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao processar solicitação'
      setShowToast({
        message: `Erro: ${errorMessage}. Tentar novamente?`,
        type: 'error'
      })

      // Auto-dismiss error toast after 10 seconds
      setTimeout(() => setShowToast(null), 10000)
    }
  }

  const handleRetry = async () => {
    await refetch()
  }

  const handleGoToCalendar = () => {
    navigate({ to: '/vacation/calendar' })
  }

  const pendingCount = queueData?.count || 0

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-brand-dark text-white">
          <div className="max-w-7xl mx-auto px-6 py-12">
            {/* Header */}
            <div className="mb-6">
              <nav className="text-sm text-brand-gray mb-2">
                <span>Dashboard</span>
                <span className="mx-2">›</span>
                <span>Férias</span>
                <span className="mx-2">›</span>
                <span className="text-white font-medium">
                  Aprovações Pendentes
                </span>
              </nav>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-display font-bold text-brand-primary">
                    Fila de Aprovações
                  </h1>
                  <p className="text-sm text-brand-gray mt-1">
                    {pendingCount} {pendingCount === 1 ? 'solicitação pendente' : 'solicitações pendentes'}
                  </p>
                </div>
                <button
                  onClick={handleGoToCalendar}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <CalendarIcon className="w-4 h-4" />
                  Ver Calendário da Equipe
                </button>
              </div>
            </div>

            {/* Toast Notification */}
            {showToast && (
              <div className="fixed top-4 right-4 z-50 animate-fade-in">
                {showToast.type === 'success' ? (
                  <div className="bg-success/10 border border-success/30 rounded-monumenta p-4 shadow-monumenta max-w-md">
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-white font-display flex-1">{showToast.message}</p>
                      <button
                        onClick={() => setShowToast(null)}
                        className="btn-icon btn-ghost text-brand-gray hover:text-white"
                        aria-label="Fechar notificação"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-error/10 border border-error/30 rounded-monumenta p-4 shadow-monumenta max-w-md">
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-error flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-white font-display flex-1">{showToast.message}</p>
                      <button
                        onClick={() => setShowToast(null)}
                        className="btn-icon btn-ghost text-brand-gray hover:text-white"
                        aria-label="Fechar notificação"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-error/10 border border-error/30 rounded-monumenta p-6 mb-6">
                <h3 className="text-lg font-display font-semibold text-error mb-2">
                  Erro ao carregar solicitações
                </h3>
                <p className="text-sm text-brand-gray mb-4">
                  {error.message || 'Ocorreu um erro ao carregar as solicitações'}
                </p>
                <button
                  onClick={handleRetry}
                  className="btn-danger inline-flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Tentar Novamente
                </button>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <ApprovalRequestCardSkeleton key={i} />
                ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && pendingCount === 0 && (
              <div className="card text-center py-12">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-success"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-display font-semibold text-white mb-2">
                    Nenhuma solicitação pendente
                  </h3>
                  <p className="text-brand-gray mb-6">
                    Você não tem solicitações de férias aguardando aprovação no momento.
                  </p>
                  <button
                    onClick={handleGoToCalendar}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <CalendarIcon className="w-5 h-5" />
                    Ver Calendário da Equipe
                  </button>
                </div>
              </div>
            )}

            {/* Approval Queue Grid */}
            {!isLoading && !error && queueData && pendingCount > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {queueData.items.map((request) => (
                  <ApprovalRequestCard
                    key={request.requestId}
                    request={request}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            )}

            {/* Confirmation Modal */}
            <ApprovalConfirmationModal
              request={selectedRequest}
              mode={modalMode}
              isOpen={modalMode !== null}
              isLoading={approveRequest.isLoading || rejectRequest.isLoading}
              onClose={() => {
                setModalMode(null)
                setSelectedRequest(null)
              }}
              onConfirm={handleConfirmAction}
            />

            {/* Request Context Panel */}
            <RequestContextPanel
              request={selectedRequest}
              isOpen={showContextPanel}
              onClose={handleCloseContextPanel}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          </div>
        </div>
    </ProtectedRoute>
  )
}

export default ApprovalQueuePage
